import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useSubmitValidation } from '../../lib/useSubmitValidation';

const MIN_REASON = 3;

/**
 * Turning down what a contractor proposed — a contract or an officer.
 *
 * The reason is required and is sent to the company as written: a refusal
 * without one leaves them guessing what to change before they propose again.
 * `onConfirm` resolves whether it landed, so a failed save keeps what was
 * typed rather than throwing it away behind an error toast.
 */
export function DeclineDialog({
  open,
  title,
  help,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  help: string;
  pending: boolean;
  onConfirm: (reason: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const v = useSubmitValidation({
    reason:
      reason.trim().length >= MIN_REASON
        ? null
        : t('common.validation.minLength', { count: MIN_REASON }),
  });
  const close = () => {
    setReason('');
    v.reset();
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {help}
          </Typography>
          <TextField
            label={t('gov.admin.decline.reason')}
            {...v.field('reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            multiline
            minRows={3}
            fullWidth
            autoFocus
            helperText={v.errors.reason ?? t('gov.admin.decline.reasonHelp')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          disabled={pending}
          onClick={v.guard(async () => {
            if (await onConfirm(reason.trim())) close();
          })}
        >
          {t('gov.admin.decline.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
